import {
  PostMessageSender,
  PostMessageHandlers,
  createChildPostMessageSender,
  PostMessageHandlersCollection,
  PostMessage
} from '../Shared/PostMessage';
import { IFluidObject } from '@fluidframework/core-interfaces';
import { ComponentCanvasProvider } from '@fluidx/office-fluid-types';
import { setupComponentInterfaceHandlers } from '../Inside/setupComponentInterfaceHandlers';
import { FindIframe } from '../Shared/FindIframe';
import { getComponentInterfaceInfo } from '../Outside/getComponentInterfaceInfo';
import { GetAbsolutePathMessage, GetAbsolutePathMessageId } from '../MessageTypes/ComponentCanvasProviderMessageTypes';

/**
 * Initializes everything needed to handle the ComponentCanvasProvider interface outside the iframe
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and the set of message handlers this interface is expecting
 */
export function getHostSideComponentCanvasProvider(
  sender: PostMessageSender,
  findIframe: FindIframe,
  handlersCollection: PostMessageHandlersCollection<string>
): { component: ComponentCanvasProvider | undefined; handlers: PostMessageHandlers<string> } {
  return { component: new HostSideComponentCanvasProviderProxy(sender, findIframe, handlersCollection), handlers: {} };
}

class HostSideComponentCanvasProviderProxy implements ComponentCanvasProvider {
  private component: IFluidObject | undefined;

  constructor(
    private sender: PostMessageSender,
    private findIframe: FindIframe,
    private handlersCollection: PostMessageHandlersCollection<string>
  ) {}

  get ComponentCanvasProvider(): ComponentCanvasProvider {
    return this;
  }

  async getCanvasComponent(): Promise<IFluidObject | undefined> {
    if (!this.component) {
      // Need to fetch a guestSide componentId to create the mapping, and handler routing
      // Host-Side does not contain this information, so it needs to be requested for the first request of getCanvasComponent
      const message: GetAbsolutePathMessage = {
        type: GetAbsolutePathMessageId
      };

      const reply = await this.sender.sendWithReturn(message);
      const guestSideComponentId = reply.payload as string;

      const componentSender: PostMessageSender = createChildPostMessageSender(this.sender, guestSideComponentId);
      // Causes recursion to populate return a set of proxies, and set up handlers in the collections for the sub-component
      this.component = getComponentInterfaceInfo(
        this.findIframe,
        componentSender,
        guestSideComponentId,
        this.handlersCollection
      ).component;
    }

    return this.component;
  }
}

/**
 * Initializes everything needed to handle the ComponentCanvasProvider interface inside the iframe
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideComponentCanvasProvider(
  component: IFluidObject,
  sender: PostMessageSender,
  handlersCollection: PostMessageHandlersCollection<string>
): PostMessageHandlers<string> | undefined {
  const componentCanvasProvider = component.ComponentCanvasProvider;
  if (!componentCanvasProvider) {
    return undefined;
  }

  return {
    [GetAbsolutePathMessageId]: (message) => {
      component.ComponentCanvasProvider?.getCanvasComponent()
        .then((canvasComponent: IFluidObject | undefined) => {
          getAbsolutePathAndSendReply(message, sender, handlersCollection, canvasComponent);
        })
        .catch(() => {
          sender.sendReply(message, { error: true });
        });
    }
  };
}

export function getAbsolutePathAndSendReply(
  message: PostMessage<string>,
  sender: PostMessageSender,
  handlersCollection: PostMessageHandlersCollection<string>,
  component: IFluidObject | undefined
) {
  if (component && component.IFluidHandle) {
    sender.sendReply(message, { payload: component.IFluidHandle.absolutePath });
  } else {
    sender.sendReply(message, { error: true });
    return;
  }

  const canvasComponentId = component.IFluidHandle!.absolutePath;
  if (handlersCollection[canvasComponentId]) {
    // No need to create handlers for the same componentId twice
    return;
  }

  const componentSender: PostMessageSender = createChildPostMessageSender(sender, canvasComponentId);
  // Causes recursion to populate handlersCollection with handlers for the subComponent
  setupComponentInterfaceHandlers(componentSender, component, canvasComponentId, handlersCollection);
}
